import { Component, Input, EventEmitter, Output } from '@angular/core';
import { PaginatorOptions, PageSizeOption } from '@tripointe/tripointe-cms/types';

const DEFAULT_PAGINATION_SIZES = [5, 10, 25, 100];
const MAX_PAGES_TO_DISPLAY = 3;

const pageSizeToFormValue = size => ({
  text: 'Show: ' + size,
  value: size
});

@Component({
  selector: 'tp-paginator',
  templateUrl: './paginator.component.html',
  styleUrls: ['./paginator.component.scss']
})
export class PaginatorComponent {
  @Input() set paginatorOptions(value: PaginatorOptions) {
    if (value) {
      this._paginatorOptions = value;
      this.updateSelectedSizeOption(value.pageSize);
      this.populatePageIndexes(value);
    }
  }
  @Output() paginationChanged = new EventEmitter<PaginatorOptions>();

  private _paginatorOptions: PaginatorOptions;
  pageSizeOptions: PageSizeOption[];
  selectedSizeOption: PageSizeOption;
  pagesCount: number;
  displayedPageNumbers: number[];

  constructor() {
    this.pageSizeOptions = DEFAULT_PAGINATION_SIZES.map(pageSizeToFormValue);
    this.selectedSizeOption = this.pageSizeOptions[0];
  }

  get paginatorOptions(): PaginatorOptions {
    return this._paginatorOptions;
  }

  get isPageChangeAvailable(): boolean {
    return this.paginatorOptions.length > DEFAULT_PAGINATION_SIZES[0];
  }

  private updateSelectedSizeOption(pageSize: number): void {
    this.selectedSizeOption = this.pageSizeOptions.find(option => option.value === pageSize);
  }

  private populatePageIndexes({ length, pageSize }: PaginatorOptions): void {
    this.pagesCount = Math.ceil(length / pageSize);
    this.displayedPageNumbers = this.getDisplayedPageNumbers();
  }

  private getDisplayedPageNumbers(): number[] {
    if (this.pagesCount < MAX_PAGES_TO_DISPLAY) {
      return new Array(this.pagesCount).fill(null).map((value, index) => Number(index) + 1);
    }

    // Navigating to next page
    if (this.paginatorOptions.previousPageIndex <= this.paginatorOptions.pageIndex) {
      const currentPageModulo = this.paginatorOptions.pageIndex % MAX_PAGES_TO_DISPLAY;
      // The conditione below occurs when next page is not within visible pages count
      if (currentPageModulo === 0) {
        const pagesLength =
          this.pagesCount - this.paginatorOptions.pageIndex < MAX_PAGES_TO_DISPLAY
            ? this.pagesCount - this.paginatorOptions.pageIndex
            : MAX_PAGES_TO_DISPLAY;

        return new Array(pagesLength).fill(null).map((value, index) => {
          return this.paginatorOptions.pageIndex + 1 + index;
        });
      } else {
        return this.displayedPageNumbers;
      }
    }

    // Navigating to previous page
    const previousPageModulo = this.paginatorOptions.previousPageIndex % MAX_PAGES_TO_DISPLAY;
    // The conditione below occurs when previous page is not within visible pages count
    if (previousPageModulo === 0) {
      const pagesLength = MAX_PAGES_TO_DISPLAY;

      return new Array(pagesLength)
        .fill(null)
        .map((value, index) => {
          return this.paginatorOptions.pageIndex + 1 - index;
        })
        .reverse();
    }

    return this.displayedPageNumbers;
  }

  changePageIndex(value: number): void {
    const newPaginationObject: PaginatorOptions = {
      ...this.paginatorOptions,
      previousPageIndex: this.paginatorOptions.pageIndex,
      pageIndex: value
    };
    this.paginationChanged.emit(newPaginationObject);
  }

  updatePageSize(selectedOption: PageSizeOption): void {
    this.paginationChanged.emit({
      ...this.paginatorOptions,
      pageSize: selectedOption.value,
      pageIndex: 0,
      previousPageIndex: 0
    });
  }
}
